import React, { useState, useEffect, useCallback, useMemo } from 'react';
import authService from '../services/authService';
import storeService from '../services/storeService';
import roleService from '../services/roleService';
import { useAuth } from '../context/AuthContext';
import { FiPlusCircle, FiEdit, FiTrash2, FiSearch, FiChevronUp, FiChevronDown } from 'react-icons/fi';
import useListView from '../hooks/useListView';

const createDefaultFormData = () => ({
  userId: '',
  name: '',
  email: '',
  password: '',
  pin: '',
  roles: [],
  stores: [],
  accessMode: 'active',
  deactivationStartDate: '',
  deactivationEndDate: '',
});

const toDateTimeLocalValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (num) => String(num).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const unwrapListResponse = (response) => {
  if (Array.isArray(response)) return response;
  return Array.isArray(response?.data) ? response.data : [];
};

const getUserStatusMeta = (user) => {
  if (user?.isActive === false) {
    return {
      label: 'Inactive (Permanent)',
      className: 'bg-red-100 text-red-800',
    };
  }
  if (user?.deactivationStartDate && user?.deactivationEndDate) {
    const now = new Date();
    const start = new Date(user.deactivationStartDate);
    const end = new Date(user.deactivationEndDate);
    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && now >= start && now <= end) {
      return {
        label: 'Inactive (Date Range)',
        className: 'bg-amber-100 text-amber-800',
      };
    }
    return {
      label: 'Active (Range Scheduled)',
      className: 'bg-blue-100 text-blue-800',
    };
  }
  return {
    label: 'Active',
    className: 'bg-green-100 text-green-800',
  };
};

// --- UserForm COMPONENT ---
const UserForm = ({ user, stores, roles, onSave, onCancel, error }) => {
  const [formData, setFormData] = useState(createDefaultFormData());

  useEffect(() => {
    if (user) { // Editing existing user
      const start = toDateTimeLocalValue(user.deactivationStartDate);
      const end = toDateTimeLocalValue(user.deactivationEndDate);
      const hasDateRange = Boolean(start && end);
      setFormData({ 
        userId: user.userId || '',
        name: user.name || '', 
        email: user.email || '', 
        password: '',
        pin: '',
        roles: user.roles ? user.roles.map(r => r._id) : [],
        stores: user.stores ? user.stores.map(s => typeof s === 'object' ? s._id : s) : [],
        accessMode: user.isActive === false ? 'permanent' : (hasDateRange ? 'range' : 'active'),
        deactivationStartDate: start,
        deactivationEndDate: end,
      });
    } else { // Adding new user
      setFormData(createDefaultFormData());
    }
  }, [user]);

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  
  const handleStoreSelect = (e) => {
    const selectedIds = Array.from(e.target.selectedOptions, option => option.value);
    setFormData(prev => ({ ...prev, stores: selectedIds }));
  };

  const handleRoleSelect = (e) => {
    const selectedIds = Array.from(e.target.selectedOptions, option => option.value);
    setFormData(prev => ({ ...prev, roles: selectedIds }));
  };

  const handleAccessModeChange = (e) => {
    const nextMode = e.target.value;
    setFormData((prev) => ({
      ...prev,
      accessMode: nextMode,
      deactivationStartDate: nextMode === 'range' ? prev.deactivationStartDate : '',
      deactivationEndDate: nextMode === 'range' ? prev.deactivationEndDate : '',
    }));
  };

  const handleSubmit = (e) => { 
    e.preventDefault(); 
    const payload = { 
      ...formData, 
      userId: formData.userId.trim(),
    };
    if (formData.accessMode === 'permanent') {
      payload.isActive = false;
      payload.deactivationStartDate = null;
      payload.deactivationEndDate = null;
    } else if (formData.accessMode === 'range') {
      payload.isActive = true;
      payload.deactivationStartDate = formData.deactivationStartDate
        ? new Date(formData.deactivationStartDate).toISOString()
        : null;
      payload.deactivationEndDate = formData.deactivationEndDate
        ? new Date(formData.deactivationEndDate).toISOString()
        : null;
    } else {
      payload.isActive = true;
      payload.deactivationStartDate = null;
      payload.deactivationEndDate = null;
    }
    delete payload.accessMode;
    if (!payload.pin) {
      delete payload.pin;
    }
    if (!payload.password) {
      delete payload.password;
    }
    onSave(payload); 
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && <p className="bg-red-100 text-red-700 p-2 rounded">{error}</p>}
      <input name="userId" value={formData.userId} onChange={handleChange} placeholder="User ID (max 20 chars)" required pattern="[A-Za-z0-9]+" maxLength={20} className="p-2 border rounded-md w-full" />
      <input name="name" value={formData.name} onChange={handleChange} placeholder="Full Name" required className="p-2 border rounded-md w-full" />
      <input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="Email" required className="p-2 border rounded-md w-full" />
      {!user && <input name="password" type="password" value={formData.password} onChange={handleChange} placeholder="Password" required={!user} className="p-2 border rounded-md w-full" />}
      <input
        name="pin"
        type="password"
        value={formData.pin}
        onChange={handleChange}
        placeholder={user ? 'Set new 6-digit PIN (optional)' : '6-digit PIN'}
        pattern="[0-9]{6}"
        inputMode="numeric"
        required={!user}
        className="p-2 border rounded-md w-full"
      />
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Assign Roles</label>
        <select multiple name="roles" value={formData.roles} onChange={handleRoleSelect} className="p-2 border rounded-md w-full h-24">
          {roles.map(role => (
            <option key={role._id} value={role._id}>{role.name}</option>
          ))}
        </select>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Assign to Stores (Hold Ctrl/Cmd to select multiple)</label>
        <select
          multiple
          name="stores"
          value={formData.stores}
          onChange={handleStoreSelect}
          required={!user}
          className="p-2 border rounded-md w-full h-32"
        >
          {stores.map(store => (
            <option key={store._id} value={store._id}>{store.name}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">User Access</label>
        <select
          name="accessMode"
          value={formData.accessMode}
          onChange={handleAccessModeChange}
          className="p-2 border rounded-md w-full"
        >
          <option value="active">Active</option>
          <option value="permanent">Permanently Deactivated</option>
          <option value="range">Deactivate for Date Range</option>
        </select>
      </div>

      {formData.accessMode === 'range' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Deactivation Start</label>
            <input
              type="datetime-local"
              name="deactivationStartDate"
              value={formData.deactivationStartDate}
              onChange={handleChange}
              required
              className="p-2 border rounded-md w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Deactivation End</label>
            <input
              type="datetime-local"
              name="deactivationEndDate"
              value={formData.deactivationEndDate}
              onChange={handleChange}
              required
              className="p-2 border rounded-md w-full"
            />
          </div>
        </div>
      )}

      <div className="flex justify-end space-x-3 pt-4">
        <button type="button" onClick={onCancel} className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300">Cancel</button>
        <button type="submit" className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark">Save User</button>
      </div>
    </form>
  );
};


const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const { user: loggedInUser } = useAuth();

  const {
    filters, sort, pagination, query, setFilter, setSort, setPage, setLimit, applyServerPagination,
  } = useListView({
    initialFilters: { search: '', roleId: '' },
    initialSort: { field: 'name', direction: 'asc' },
    initialPagination: { page: 1, limit: 25 },
  });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await authService.getUsers({ ...query, includeInactive: true });
      setUsers(response?.data || []);
      applyServerPagination(response?.pagination || {});
    } catch (err) {
      setError('Failed to fetch users.');
    } finally {
      setLoading(false);
    }
  }, [applyServerPagination, query]);

  const fetchLookups = useCallback(async () => {
    try {
      const [storesData, rolesData] = await Promise.all([storeService.getStores(), roleService.getRoles()]);
      setStores(unwrapListResponse(storesData));
      setRoles(unwrapListResponse(rolesData));
    } catch (err) {
      setError('Failed to fetch user setup data.');
    }
  }, []);

  useEffect(() => { fetchLookups(); }, [fetchLookups]);
  useEffect(() => {
    const normalizedSearch = searchInput.trim();
    if (normalizedSearch === String(filters.search || '').trim()) return undefined;
    const timeout = setTimeout(() => setFilter('search', normalizedSearch, { resetPage: true }), 250);
    return () => clearTimeout(timeout);
  }, [searchInput, filters.search, setFilter]);
  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const sortIcon = useCallback((field) => {
    if (sort.field !== field) return null;
    return sort.direction === 'asc' ? <FiChevronUp className="inline ml-1" /> : <FiChevronDown className="inline ml-1" />;
  }, [sort.direction, sort.field]);

  const totalCount = Number(pagination?.total) || 0;
  const currentPage = Number(pagination?.page) || 1;
  const totalPages = Number(pagination?.pages) || 1;
  const pageSize = Number(pagination?.limit) || 25;
  const visibleStart = users.length > 0 ? ((currentPage - 1) * pageSize) + 1 : 0;
  const visibleEnd = users.length > 0 ? visibleStart + users.length - 1 : 0;
  const selectedRole = useMemo(() => roles.find((role) => role._id === filters.roleId), [filters.roleId, roles]);

  const handleOpenModal = (user = null) => { setEditingUser(user); setFormError(''); setIsModalOpen(true); };
  const handleCloseModal = () => { setEditingUser(null); setFormError(''); setIsModalOpen(false); };

  const handleSaveUser = async (userData) => {
    if (!editingUser && (!Array.isArray(userData.stores) || userData.stores.length === 0)) {
      setFormError('Assign at least one store when creating a user.');
      return;
    }
    try {
      if (editingUser) await authService.updateUser(editingUser._id, userData);
      else await authService.register(userData);
      await fetchUsers();
      handleCloseModal();
    } catch (err) {
      setFormError(err.response?.data?.message || err.message || 'Failed to save user.');
    }
  };

  const handleDeactivateUser = async (id) => {
    if (loggedInUser?.id === id) { setError('You cannot deactivate your own account.'); return; }
    if (window.confirm('Are you sure you want to deactivate this user?')) {
      try {
        await authService.deactivateUser(id);
        await fetchUsers();
      } catch (err) { setError('Failed to deactivate user.'); }
    }
  };

  const sortableHeader = (field, label) => (
    <button type="button" onClick={() => setSort(field)} className="hover:text-gray-700">{label}{sortIcon(field)}</button>
  );

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">User Management</h1>
        <button onClick={() => handleOpenModal()} className="flex items-center bg-primary text-white px-4 py-2 rounded-md shadow hover:bg-primary-dark">
          <FiPlusCircle className="mr-2" /> Add New User
        </button>
      </div>
      {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}
      <div className="mb-4 bg-white shadow-sm rounded-lg p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex w-full flex-col gap-3 md:flex-row md:items-center">
            <div className="relative w-full md:max-w-md">
              <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search by user ID, name, email, or phone" className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 text-sm shadow-sm focus:border-primary focus:ring-primary" aria-label="Search users" />
            </div>
            <div className="flex w-full items-center gap-2 md:w-auto">
              <label htmlFor="users-role-filter" className="text-sm text-gray-500">Role</label>
              <select id="users-role-filter" value={filters.roleId || ''} onChange={(e) => setFilter('roleId', e.target.value, { resetPage: true })} className="min-w-40 flex-1 rounded-md border border-gray-300 py-2 px-3 text-sm shadow-sm focus:border-primary focus:ring-primary md:flex-none">
                <option value="">All roles</option>
                {roles.map((role) => <option key={role._id} value={role._id}>{role.name}</option>)}
              </select>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <label htmlFor="users-page-size" className="text-sm text-gray-500">Rows</label>
            <select id="users-page-size" value={pageSize} onChange={(e) => setLimit(e.target.value)} className="border rounded-md py-2 px-3 text-sm">
              {[25, 50, 100].map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
          </div>
        </div>
      </div>
      <div className="bg-white shadow-md rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50"><tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{sortableHeader('userId', 'User ID')}</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{sortableHeader('name', 'Name')}</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{sortableHeader('email', 'Email')}</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Roles</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned Stores</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
          </tr></thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading && <tr><td colSpan="7" className="px-6 py-10 text-center text-sm text-gray-500">Loading users...</td></tr>}
            {!loading && users.map((user) => {
              const status = getUserStatusMeta(user);
              return <tr key={user._id}>
                <td className="px-6 py-4 font-mono text-sm">{user.userId || '-'}</td><td className="px-6 py-4">{user.name}</td><td className="px-6 py-4">{user.email}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{user.roles?.map((role) => role.name).join(', ')}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{user.stores?.map((store) => store.name).join(', ')}</td>
                <td className="px-6 py-4"><span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${status.className}`}>{status.label}</span></td>
                <td className="px-6 py-4 text-right space-x-4"><button aria-label={'Edit ' + user.name} onClick={() => handleOpenModal(user)} className="text-indigo-600"><FiEdit size={18} /></button><button aria-label={'Deactivate ' + user.name} onClick={() => handleDeactivateUser(user._id)} className="text-red-600"><FiTrash2 size={18} /></button></td>
              </tr>;
            })}
            {!loading && users.length === 0 && <tr><td colSpan="7" className="px-6 py-10 text-center text-sm text-gray-500">No users found{filters.search ? ' for "' + filters.search + '"' : ''}{selectedRole ? ' with role "' + selectedRole.name + '"' : ''}.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-gray-600">
        <div>{totalCount > 0 ? 'Showing ' + visibleStart + '-' + visibleEnd + ' of ' + totalCount + ' users' : 'No users to display'}</div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setPage(currentPage - 1)} disabled={loading || currentPage <= 1} className="px-3 py-1.5 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed">Previous</button>
          <span>Page {currentPage} of {Math.max(totalPages, 1)}</span>
          <button type="button" onClick={() => setPage(currentPage + 1)} disabled={loading || currentPage >= totalPages} className="px-3 py-1.5 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed">Next</button>
        </div>
      </div>
      {isModalOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center"><div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg"><h2 className="text-2xl font-bold mb-4">{editingUser ? 'Edit User' : 'Add New User'}</h2><UserForm user={editingUser} stores={stores} roles={roles} onSave={handleSaveUser} onCancel={handleCloseModal} error={formError} /></div></div>}
    </div>
  );
};

export default UsersPage;
